import { FC, useEffect, useMemo, useRef, useState } from 'react'
import {
  Dialog as UnstyledDialog,
  DialogContent as UnstyledDialogContent,
  DialogTitle as UnstyledDialogTitle,
  DialogActions,
  Typography,
} from '@mui/material'
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'
import { ArrowLeft } from '@emotion-icons/bootstrap/ArrowLeft'
import { PencilAlt } from '@emotion-icons/fa-solid/PencilAlt'
import { Trash } from '@emotion-icons/fa-solid/Trash'
import { Upload } from '@emotion-icons/heroicons-outline/Upload'
import { LocationOn } from '@emotion-icons/material/LocationOn'
import { MapMarkedAlt } from '@emotion-icons/fa-solid/MapMarkedAlt'
import { PlaylistAdd } from '@emotion-icons/material/PlaylistAdd'
import { Colors, Shapes } from 'src/components/theme'
import { UserList } from 'src/common-services'
import { useDeleteUserList } from 'src/components/user-lists-modal/hooks'
import {
  ActionButtonsWrapper,
  BackButton,
  DenseButton,
  HeadingWrapper,
  NavigationWrapper,
  RightActionButtons,
  Subtitle,
  Title,
  TitleAndActionButtonsWrapper,
  TitleAndSubtitleWrapper,
} from 'src/components/user-lists-modal/components'
import { useRouter } from 'next/router'
import { Spinner } from 'src/components/spinner'
import { useBrandsRankLayer } from 'src/hooks/use-brands-rank-layer'
import styled from '@emotion/styled'
import { FileCsv } from '@emotion-icons/fa-solid/FileCsv'
import { json2csv } from 'json-2-csv'
import { downloadContent } from 'src/utils/csv'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'

import * as userListsModalAtoms from 'src/atoms/user-lists/user-lists-modal'
import * as createUserListModalAtoms from 'src/atoms/user-lists/user-list-modal'
import * as userListsLayerAtoms from 'src/atoms/user-lists-layer'
import * as mapToolsAtoms from 'src/atoms/main-map-tools'
import { useMapExplorerUserLists } from 'src/hooks/use-map-explorer-user-lists'

const Dialog = styled(UnstyledDialog)`
  & .MuiPaper-root {
    width: 320px;
  }
`

const DialogContent = styled(UnstyledDialogContent)`
  padding: 16px;
  padding-bottom: 8px;
`

const DialogTitleContainer = styled(UnstyledDialogTitle)`
  padding: 0px;
  padding-bottom: 12px;
`

const ActionsContainer = styled(DialogActions)`
  display: flex;
  gap: ${Shapes.spacing[2]}px;
  padding: 16px;
  padding-top: 16px;
`

const ActionButton = styled(ButtonWithIcon)``

type BaseHeaderProps = {
  title: string
  subtitle?: string
  topActions?: React.ReactNode
  bottomActions?: React.ReactNode
  backText?: string
  onClickBack?: () => void
}

const BaseHeader: FC<BaseHeaderProps> = ({
  title,
  subtitle,
  bottomActions,
  topActions,
  backText = 'Back',
  onClickBack,
}) => {
  const currentSection = useRecoilValue(userListsModalAtoms.currentSection)

  const searchable = currentSection === 'user-lists' || currentSection === 'user-list-detail'

  return (
    <>
      <NavigationWrapper>
        {onClickBack && (
          <BackButton ghost color={Colors.bgWhite} onClick={onClickBack}>
            <ArrowLeft color={Colors.bgWhite} size={20} />
            <Typography>{backText}</Typography>
          </BackButton>
        )}
      </NavigationWrapper>
      <HeadingWrapper style={{ marginBottom: searchable ? 12 : 0, height: 'auto' }}>
        <TitleAndSubtitleWrapper style={{ marginTop: searchable ? 16 : 0, marginBottom: searchable ? 8 : 0 }}>
          <TitleAndActionButtonsWrapper>
            <Title>{title}</Title>
            {topActions && <ActionButtonsWrapper>{topActions}</ActionButtonsWrapper>}
          </TitleAndActionButtonsWrapper>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </TitleAndSubtitleWrapper>
        {bottomActions}
      </HeadingWrapper>
    </>
  )
}

const UserListsHeader: FC = () => {
  const setUserListModalState = useSetRecoilState(createUserListModalAtoms.state)
  const handleClickNewList = () => setUserListModalState({})

  return (
    <BaseHeader
      title="Manage Lists"
      subtitle="Use lists for your portfolios, reports, etc."
      bottomActions={
        <DenseButton color={Colors.textWhite} onClick={handleClickNewList}>
          <PlaylistAdd size={18} />
          New List
        </DenseButton>
      }
    />
  )
}

type UserListDetailHeaderProps = {
  onInsertListInMapBuilder?: (list: UserList) => void
  insertingListLoading?: boolean
}

const UserListDetailHeader: FC<UserListDetailHeaderProps> = ({ onInsertListInMapBuilder, insertingListLoading }) => {
  const searchFieldRef = useRef<HTMLInputElement>(null)
  const setOpen = useSetRecoilState(userListsModalAtoms.open)
  const [listFilter, setListFilter] = useRecoilState(userListsModalAtoms.listFilter)
  const setSelectedListId = useSetRecoilState(userListsModalAtoms.selectedListId)
  const selectedList = useRecoilValue(userListsModalAtoms.selectedList)
  const setUploadSection = useSetRecoilState(userListsModalAtoms.uploadSection)
  const setUserListModalState = useSetRecoilState(createUserListModalAtoms.state)
  const [userListToDelete, setUserListToDelete] = useState<UserList | null>(null)
  const [exportListLoading, setExportListLoading] = useState(false)
  const router = useRouter()
  const isMapBuilderPage = router.pathname.includes('map-builder')
  const { mutate: deleteUserList } = useDeleteUserList({
    onSuccess: () => {
      setSelectedListId(null)
      closeDeleteUserListIntent()
    },
  })

  // user list in map explorer state section
  const setUserLisetsLayerActive = useSetRecoilState(userListsLayerAtoms.active)
  const { closeBrandsRankLayer } = useBrandsRankLayer()
  const setSelectedTool = useSetRecoilState(mapToolsAtoms.mapPinsTools)
  const { addUserList } = useMapExplorerUserLists()

  const insertListEnabled = useMemo(() => {
    const places = selectedList?.items.filter((item) => item.type === 'place' || item.type === 'location') ?? []
    return places.length > 0
  }, [selectedList])

  const handleClickBack = () => {
    setListFilter('')
    setSelectedListId(null)
  }

  const handleInsertListInMapBuilder = () => {
    if (selectedList && insertListEnabled) {
      onInsertListInMapBuilder?.(selectedList)
    }
  }

  const handleInsertListInMapExplorer = () => {
    if (!selectedList) {
      return
    }
    closeBrandsRankLayer()

    addUserList(selectedList.id)
    setUserLisetsLayerActive(true)
    setSelectedTool('user-lists')

    setOpen(false)
  }

  const handleEditUserList = () =>
    setUserListModalState({ id: selectedList?.id, name: selectedList?.name, description: selectedList?.description })

  const handleDeleteUserListIntent = () => selectedList && setUserListToDelete(selectedList)

  const closeDeleteUserListIntent = () => setUserListToDelete(null)

  const handleDeleteUserList = () => selectedList && deleteUserList(selectedList.id)

  const handleExportList = () => {
    if (!selectedList) {
      return
    }
    setExportListLoading(true)
    const columns = ['Address']
    const rows = selectedList.items
      .map((item) => {
        if (item.type === 'place') {
          return {
            Address: item.data.address,
          }
        }
      })
      .filter(Boolean) as any[]
    const csv = json2csv(rows, {
      keys: columns,
    })
    downloadContent(csv, `${selectedList.name}.csv`)
    setExportListLoading(false)
  }

  useEffect(() => {
    if (listFilter === '' && searchFieldRef.current) {
      searchFieldRef.current.value = ''
    }
  }, [listFilter])

  if (!selectedList) {
    return null
  }

  return (
    <>
      <BaseHeader
        title={selectedList.name}
        subtitle={selectedList.description ?? ''}
        backText="All Lists"
        onClickBack={handleClickBack}
        topActions={
          <>
            <DenseButton color={Colors.textWhite} onClick={handleEditUserList}>
              <PencilAlt size={12} />
              Edit
            </DenseButton>
            <RightActionButtons>
              {isMapBuilderPage ? (
                <DenseButton
                  color={Colors.textWhite}
                  disabled={!insertListEnabled}
                  onClick={handleInsertListInMapBuilder}>
                  {insertingListLoading ? <Spinner size={16} /> : <LocationOn size={16} />}
                  Insert in Map Builder
                </DenseButton>
              ) : (
                <>
                  <DenseButton
                    color={Colors.textWhite}
                    style={{ marginLeft: 16 }}
                    onClick={() => {
                      setUploadSection('upload-csv')
                    }}>
                    <Upload size={16} />
                    Upload Locations
                  </DenseButton>
                  <DenseButton color={Colors.textWhite} onClick={() => setUploadSection('upload-shapefile')}>
                    <Upload size={16} />
                    Upload Shapefile
                  </DenseButton>
                  <DenseButton
                    color={Colors.textWhite}
                    disabled={!insertListEnabled}
                    onClick={handleInsertListInMapExplorer}>
                    {insertingListLoading ? <Spinner size={16} /> : <MapMarkedAlt size={16} />}
                    Insert in Map Explorer
                  </DenseButton>
                  {selectedList?.items.length > 0 && (
                    <DenseButton color={Colors.textWhite} disabled={exportListLoading} onClick={handleExportList}>
                      {exportListLoading ? <Spinner size={16} /> : <FileCsv size={16} />}
                      Export
                    </DenseButton>
                  )}
                </>
              )}
              <DenseButton color={Colors.primaryRed100} onClick={handleDeleteUserListIntent}>
                <Trash size={12} />
                Delete
              </DenseButton>
            </RightActionButtons>
          </>
        }
      />
      <Dialog open={!!userListToDelete} onClose={closeDeleteUserListIntent}>
        <DialogContent>
          <DialogTitleContainer>
            <Typography variant="title2" color={Colors.neutral800}>
              Delete List
            </Typography>
          </DialogTitleContainer>
          <Typography variant="textmdm">
            Are you sure you want to delete your list '{userListToDelete?.name}'? This action is irreversible.
          </Typography>
        </DialogContent>
        <ActionsContainer>
          <ActionButton onClick={handleDeleteUserList} color={Colors.redDark} inverted autoFocus fullWidth>
            Delete
          </ActionButton>
          <ActionButton onClick={closeDeleteUserListIntent} fullWidth>
            Cancel
          </ActionButton>
        </ActionsContainer>
      </Dialog>
    </>
  )
}

const UploadCSVHeader: FC = () => {
  const selectedList = useRecoilValue(userListsModalAtoms.selectedList)
  const setUploadSection = useSetRecoilState(userListsModalAtoms.uploadSection)

  const handleClickBack = () => setUploadSection(undefined)

  if (!selectedList) {
    return null
  }

  return <BaseHeader title="Upload CSV" backText={selectedList.name} onClickBack={handleClickBack} />
}

const UploadShapefileHeader: FC = () => {
  const selectedList = useRecoilValue(userListsModalAtoms.selectedList)
  const setUploadSection = useSetRecoilState(userListsModalAtoms.uploadSection)

  const handleClickBack = () => setUploadSection(undefined)

  if (!selectedList) {
    return null
  }

  return <BaseHeader title="Upload Shapefile" backText={selectedList.name} onClickBack={handleClickBack} />
}
const MatchDataResultsHeader: FC = () => {
  const selectedList = useRecoilValue(userListsModalAtoms.selectedList)
  const setUploadSection = useSetRecoilState(userListsModalAtoms.uploadSection)
  const csvFile = useRecoilValue(userListsModalAtoms.csvFile)
  const resetMatchedData = useResetRecoilState(userListsModalAtoms.matchedData)

  const handleClickBack = () => {
    setUploadSection('match-data')
    resetMatchedData()
  }

  if (!selectedList || !csvFile) {
    return null
  }

  return (
    <BaseHeader title={`Matched Data Results - ${csvFile.name}`} backText="Match Data" onClickBack={handleClickBack} />
  )
}

const MatchDataHeader: FC = () => {
  const selectedList = useRecoilValue(userListsModalAtoms.selectedList)
  const setUploadSection = useSetRecoilState(userListsModalAtoms.uploadSection)
  const csvFile = useRecoilValue(userListsModalAtoms.csvFile)
  const resetCsvFile = useResetRecoilState(userListsModalAtoms.csvFile)

  const handleClickBack = () => {
    setUploadSection('upload-csv')
    resetCsvFile()
  }

  if (!selectedList || !csvFile) {
    return null
  }

  return (
    <BaseHeader title={`Matched Data Results - ${csvFile.name}`} backText="Upload CSV" onClickBack={handleClickBack} />
  )
}

type HeaderProps = UserListDetailHeaderProps

export const Header: FC<HeaderProps> = ({ insertingListLoading, onInsertListInMapBuilder }) => {
  const currentSection = useRecoilValue(userListsModalAtoms.currentSection)

  if (currentSection === 'user-lists') {
    return <UserListsHeader />
  }
  if (currentSection === 'user-list-detail') {
    return (
      <UserListDetailHeader
        insertingListLoading={insertingListLoading}
        onInsertListInMapBuilder={onInsertListInMapBuilder}
      />
    )
  }
  if (currentSection === 'upload-csv') {
    return <UploadCSVHeader />
  }
  if (currentSection === 'match-data') {
    return <MatchDataHeader />
  }
  if (currentSection === 'match-data-results') {
    return <MatchDataResultsHeader />
  }
  if (currentSection === 'upload-shapefile') {
    return <UploadShapefileHeader />
  }

  return null
}
